import React, { useState, useCallback, useMemo } from "react";
import { useIntl } from "react-intl";
import {
  Box,
  Checkbox,
  Chip,
  FormControlLabel,
  Popover,
  Stack,
  Typography,
  alpha,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import CloseIcon from "@mui/icons-material/Close";
import { be_regions, fr_regions, lu_regions } from "bundles/common/utils/constants";
import { formatRegionMessage } from "bundles/common/utils/utils";

const SOURCES = [
  { label: "Forem", value: "forem" },
  { label: "VDAB", value: "vdab" },
  { label: "StepStone", value: "stepstone" },
  { label: "Actiris", value: "actiris" },
  { label: "France Travail", value: "france_travail" },
  { label: "APEC", value: "apec" },
  { label: "Indeed", value: "indeed" },
  { label: "LinkedIn", value: "linkedin" },
];

const OUTCOME_OPTIONS = [
  { label: "Not contacted", value: "not_contacted" },
  { label: "No answer", value: "no_answer" },
  { label: "Voicemail", value: "voicemail" },
  { label: "Not interested", value: "not_interested" },
  { label: "Signed up", value: "signed_up" },
  { label: "Follow up today", value: "follow_up_today" },
  { label: "Follow up later", value: "follow_up_later" },
  { label: "Sign up later", value: "sign_up_later" },
  { label: "To be checked", value: "to_be_checked" },
  { label: "Applied", value: "applied" },
];

const DEFAULT_OUTCOME_VALUES = OUTCOME_OPTIONS
  .map((o) => o.value)
  .filter((v) => v !== "signed_up" && v !== "applied");

const chipSx = (active) => ({
  fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
  fontSize: 13,
  borderRadius: "10px",
  height: 34,
  px: 0.5,
  cursor: "pointer",
  transition: "all 0.15s ease",
  ...(active
    ? {
      backgroundColor: alpha("#079344", 0.12),
      color: "#079344",
      borderColor: "#079344",
      "&:hover": { backgroundColor: alpha("#079344", 0.18) },
    }
    : {
      backgroundColor: "rgba(255,255,255,0.9)",
      color: "#2A334E",
      borderColor: "rgba(42,51,78,0.2)",
      "&:hover": {
        backgroundColor: "#fff",
        borderColor: "rgba(42,51,78,0.35)",
      },
    }),
});

const FilterChip = ({ def, selected, onChange, dynamicOptions }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const count = selected.length;

  const handleToggle = (value) => {
    const next = selected.includes(value)
      ? selected.filter((v) => v !== value)
      : [...selected, value];
    onChange(next);
  };

  const options = def.dynamic
    ? (dynamicOptions || [])
    : def.grouped
      ? def.groups.flatMap((g) => g.options)
      : def.options;

  return (
    <>
      <Chip
        variant="outlined"
        label={count > 0 ? `${def.label} (${count})` : def.label}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={chipSx(count > 0)}
      />
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.5,
              borderRadius: "12px",
              boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
              minWidth: 220,
              maxHeight: 360,
              overflow: "auto",
            },
          },
        }}
      >
        <Box sx={{ py: 0.5 }}>
          {def.grouped
            ? def.groups.map((group) => (
              <Box key={group.label}>
                <Typography
                  sx={{
                    px: 2,
                    pt: 1,
                    pb: 0.5,
                    fontSize: 11,
                    fontWeight: 700,
                    fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                    color: "text.secondary",
                    textTransform: "uppercase",
                    letterSpacing: "0.6px",
                  }}
                >
                  {group.label}
                </Typography>
                {group.options.map((opt) => (
                  <FormControlLabel
                    key={opt.value}
                    sx={{ display: "flex", mx: 0, px: 2, py: 0.25 }}
                    control={
                      <Checkbox
                        size="small"
                        checked={selected.includes(opt.value)}
                        onChange={() => handleToggle(opt.value)}
                        sx={{
                          color: "rgba(42,51,78,0.3)",
                          "&.Mui-checked": { color: "#079344" },
                        }}
                      />
                    }
                    label={
                      <Typography sx={{ fontSize: 13, color: "#2A334E", fontFamily: "Segoe UI Regular, sans-serif, system-ui" }}>
                        {opt.label}
                      </Typography>
                    }
                  />
                ))}
              </Box>
            ))
            : options.map((opt) => (
              <FormControlLabel
                key={opt.value}
                sx={{ display: "flex", mx: 0, px: 2, py: 0.25 }}
                control={
                  <Checkbox
                    size="small"
                    checked={selected.includes(String(opt.value))}
                    onChange={() => handleToggle(String(opt.value))}
                    sx={{
                      color: "rgba(42,51,78,0.3)",
                      "&.Mui-checked": { color: "#079344" },
                    }}
                  />
                }
                label={
                  <Typography sx={{ fontSize: 13, color: "#2A334E", fontFamily: "Segoe UI Regular, sans-serif, system-ui" }}>
                    {opt.label}
                  </Typography>
                }
              />
            ))}
        </Box>
      </Popover>
    </>
  );
};

const ExternalProfilesTableFilters = ({ filters, onFiltersChange, internalUsers = [] }) => {
  const intl = useIntl();

  const filterDefs = useMemo(
    () => [
      {
        id: "source",
        label: intl.formatMessage({ id: "internal.filters.source", defaultMessage: "Source" }),
        options: SOURCES,
      },
      {
        id: "region",
        label: intl.formatMessage({ id: "candidate.region", defaultMessage: "Region" }),
        grouped: true,
        groups: [
          {
            label: formatRegionMessage("belgium"),
            options: be_regions.map((r) => ({ value: r, label: formatRegionMessage(r) })),
          },
          {
            label: formatRegionMessage("luxembourg"),
            options: lu_regions.map((r) => ({ value: r, label: formatRegionMessage(r) })),
          },
          {
            label: formatRegionMessage("france"),
            options: fr_regions.map((r) => ({ value: r, label: formatRegionMessage(r) })),
          },
        ],
      },
      {
        id: "last_action",
        label: intl.formatMessage({ id: "internal.filters.last_action", defaultMessage: "Last action" }),
        options: OUTCOME_OPTIONS,
      },
      {
        id: "added_by",
        label: intl.formatMessage({ id: "internal.filters.added_by", defaultMessage: "Added by" }),
        dynamic: true,
        options: [],
      },
    ],
    [intl],
  );

  const totalActive = useMemo(
    () => Object.values(filters).reduce((sum, arr) => sum + arr.length, 0),
    [filters],
  );

  const handleChange = useCallback(
    (filterId) => (newValues) => {
      onFiltersChange({ ...filters, [filterId]: newValues });
    },
    [filters, onFiltersChange],
  );

  const handleClearAll = useCallback(() => {
    onFiltersChange({ ...EMPTY_EP_FILTERS });
  }, [onFiltersChange]);

  const addedByOptions = useMemo(
    () => internalUsers.map((u) => ({ label: u.full_name || u.email, value: String(u.id) })),
    [internalUsers],
  );

  return (
    <Box
      sx={{
        px: 3,
        py: 1,
        backgroundColor: "#F4F5FA",
        borderBottom: "1px solid rgba(42,51,78,0.08)",
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <FilterListIcon sx={{ fontSize: 18, color: "#2A334E", opacity: 0.5, mr: 0.5 }} />

        {filterDefs.map((def) => (
          <FilterChip
            key={def.id}
            def={def}
            selected={filters[def.id] || []}
            onChange={handleChange(def.id)}
            dynamicOptions={def.id === "added_by" ? addedByOptions : undefined}
          />
        ))}

        {totalActive > 0 && (
          <Chip
            size="small"
            label="Clear all"
            deleteIcon={<CloseIcon sx={{ fontSize: "16px !important" }} />}
            onDelete={handleClearAll}
            onClick={handleClearAll}
            sx={{
              ml: 0.5,
              fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
              fontSize: 12,
              color: "#D32F2F",
              borderColor: "#D32F2F",
              height: 28,
              borderRadius: "8px",
              "& .MuiChip-deleteIcon": { color: "#D32F2F" },
            }}
            variant="outlined"
          />
        )}
      </Stack>
    </Box>
  );
};

export const EMPTY_EP_FILTERS = {
  source: [],
  region: [],
  last_action: DEFAULT_OUTCOME_VALUES,
  added_by: [],
};

export default ExternalProfilesTableFilters;
